import { useSyncExternalStore } from 'react'
import { ToastFn, ToastType } from '../common/common'

// Module-level toast store. Toasts are an outbound side effect - the
// caller fires, the UI shows, nothing reads back. So they don't need
// React context at all: anything (component, service, websocket
// callback, async catch) can `import { addToast } from
// '../services/Toasts'` and call it. `ToastMessages` is the single
// subscriber via `useToasts()`.

export interface Toast {
  id: string
  type: ToastType
  message: string
}

let toasts: Toast[] = []
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach(l => { try { l() } catch { /* never let one bad listener break the rest */ } })
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export const addToast: ToastFn = (type, message, duration = 3000) => {
  const id = newId()
  toasts = [{ id, type, message }, ...toasts]
  notify()
  setTimeout(() => removeToast(id), duration + 300)
}

export function removeToast(id: string): void {
  if (!toasts.some(t => t.id === id)) return
  toasts = toasts.filter(t => t.id !== id)
  notify()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

// React hook for the single rendering surface. `useSyncExternalStore`
// makes the snapshot reference stable as long as the store hasn't
// changed, so consumers get correct tearing semantics for free.
export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribe, () => toasts, () => toasts)
}
